// useApiRequest.js
import toastService from '@/composables/toastService';
import UserUtils from '@/composables/UserUtils';

// Utils
let router = null
let store = null
let logout;


// --- HTTPS Worker creation ---
const apiWebWorker = new Worker(new URL("@/composables/HTTPS/apiWebWorker", import.meta.url)) // The functions makes use of the worker which makes the requests on its own thread (not the main one)

// --- Concurrent requests managemenent ---
const pendingRequests = new Map();

// --- Unique req/res id: util ---
function generateUniqueRequestId() {
    return Math.random().toString(36).substring(2,15) + Date.now().toString(36);
}

// --- Worker interactions ---
// Trigger the worker for API call
function apiCallWithWorker(requestName, requestUrl, requestBody, requestOptions, additionalInfoForWebWorker) {
    const requestId = generateUniqueRequestId();

    return new Promise((resolve, reject) => {
        // Store the resolve/reject functions in the pending requests map
        pendingRequests.set(requestId, { resolve, reject });
        // Send the request to the worker
        apiWebWorker.postMessage({ requestName, requestId, requestUrl, requestBody, requestOptions, additionalInfoForWebWorker })
    })
}
// Response from the worker
apiWebWorker.onmessage = (event) => {
    const about = event.data.about

    switch (about) {
        case "server_response":
            const { requestId, success, data, error } = event.data;

            // Retrieve the correct request for the response
            const request = pendingRequests.get(requestId);

            // If request exists, trigger its resolve/reject
            if (request) {
                // Check if success or error
                if (success) {
                    request.resolve(data);
                } else {
                    request.reject(error);
                }
                // Delete the pending request
                pendingRequests.delete(requestId);
            }
            break;
        case "logout":
            logout()
            break;
        case "alert":
            const { severity, summary, detail, life } = event.data;
            toastService.add({ severity: severity, summary: summary, detail: detail, life: life });
            break;
        default:
            break;
    }
}

// --- ALL FUNCTIONS THAT CAN BE CALLED BY THE MAIN THREAD ---
const useApiRequest = () => {
    const api_server_url = process.env.VUE_APP_API_SERVER_URL

    // --- SETUP FOR LOGOUT ---
    const setupUseApiRequest = (setupStore, setupRouter) => {
        store = setupStore
        router = setupRouter
        const { logoutUser } = UserUtils(store, router)
        logout = logoutUser
    }
    
    // --- API REQUESTS ---

    // -- Dev --
    async function fetchExampleWorker() {
      const reqBody = {
          action_name: 'ping'
      }
      try {
        const response = await apiCallWithWorker('example_worker', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Ping --
    async function fetchPing() {
      const reqBody = {
          action_name: 'ping'
      }
      try {
        const response = await apiCallWithWorker('ping', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Registration --
    async function fetchRegister(requestInfo) {
      const reqBody = {
          action_name: 'register',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('register', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchRegisterConfirm(requestInfo) {
      const reqBody = {
          action_name: 'register_confirm',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('login', api_server_url, reqBody, { needsAuth: false }) // 'login' because it acts like a 'login' concerning the response management: potentially connected, and access token management, etc.
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Login --
    async function fetchAutoLogin() {
      const reqBody = {
        action_name: "auto_login"
      }
      try {
        const response = await apiCallWithWorker('login', `${api_server_url}/refresh`, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchLogin(requestInfo) {
      const reqBody = {
          action_name: 'login',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('login', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Web View Login through browser: Authorization Code Flow --
    async function fetchAuthorization(requestInfo) {
      const reqBody = {
          action_name: 'authorization',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('authorization', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchAuthorizationRegister(requestInfo) {
      const reqBody = {
          action_name: 'authorization_register',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('authorization_register', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchAuthorizationAutoLogin(requestInfo) {
      const reqBody = {
          action_name: 'authorization_auto_login',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('authorization_auto_login', `${api_server_url}/refresh`, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchAuthorizationLogin(requestInfo) {
      const reqBody = {
          action_name: 'authorization_login',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('authorization_login', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchAuthorizationExchange(requestInfo, additionalInfoForWebWorker) {
      const reqBody = {
          action_name: 'authorization_exchange',
          object: requestInfo // requestInfo contains the authorization_code
      }
      try {
        const response = await apiCallWithWorker('authorization_exchange', api_server_url, reqBody, { needsAuth: false }, additionalInfoForWebWorker)
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Reset password --
    async function fetchForgotPassword(requestInfo) {
      const reqBody = {
          action_name: 'forgot_password',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('forgot_password', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchResetPassword(requestInfo) {
      const reqBody = {
          action_name: 'reset_password',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('reset_password', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Send email again --
    async function fetchSendAgain(requestInfo) {
      const reqBody = {
          action_name: 'send_again',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('send_again', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Get info --
    async function fetchGetLicenses() {
      const reqBody = {
          action_name: 'get_licenses'
      }
      try {
        const response = await apiCallWithWorker('get_licenses', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchGetUsers() {
      const reqBody = {
          action_name: 'get_users'
      }
      try {
        const response = await apiCallWithWorker('get_users', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchGetProfile() {
      const reqBody = {
          action_name: 'get_profile'
      }
      try {
        const response = await apiCallWithWorker('get_profile', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchGetApplicationLicenses(requestInfo) { // In Web View
      const reqBody = {
          action_name: 'get_application_licenses',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('get_application_licenses', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchGetApplicationInfo(requestInfo) { // In Web View
      const reqBody = {
          action_name: 'get_application_info',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('get_application_info', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Add/Remove serial number --
    async function fetchRequestAccess(requestInfo) {
      const reqBody = {
          action_name: 'request_access',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('request_access', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchAccessSharingConfirm(requestInfo) {
      const reqBody = {
          action_name: 'access_sharing_confirm',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('access_sharing_confirm', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchAddSerialNumber(requestInfo) {
      const reqBody = {
          action_name: 'add_serial_number',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('add_serial_number', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchDeleteSerialNumber(requestInfo) {
      const reqBody = {
          action_name: 'delete_serial_number',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('delete_serial_number', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchTransferOwnership(requestInfo) {
      const reqBody = {
          action_name: 'transfer_ownership',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('transfer_ownership', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Add/Remove user --
    async function fetchSendInvitation(requestInfo) {
      const reqBody = {
          action_name: 'send_invitation',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('send_invitation', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchAddUser(requestInfo) {
      const reqBody = {
          action_name: 'add_user',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('add_user', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchDeleteUser(requestInfo) {
      const reqBody = {
          action_name: 'delete_user',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('delete_user', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Delegates management --
    async function fetchEditDelegates(requestInfo) {
      const reqBody = {
          action_name: 'edit_delegates',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('edit_delegates', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Edit Profile --
    async function fetchEditProfile(requestInfo) {
      const reqBody = {
          action_name: 'edit_profile',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('edit_profile', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    // Confirm the update of account email
    async function fetchUpdateEmailConfirm(requestInfo) {
      const reqBody = {
          action_name: 'update_email_confirm',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('update_email_confirm', api_server_url, reqBody, { needsAuth: false })
        return response;
      } catch (err) {
        throw err;
      }
    }
    // Delete the account
    async function fetchDeleteAccount(requestInfo) {
      const reqBody = {
          action_name: 'delete_account',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('delete_account', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Logout --
    async function fetchLogout() {
      const reqBody = {
          action_name: 'logout'
      }
      try {
        const response = await apiCallWithWorker('logout', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }


    // -- Activate/Reactivate/Release/ForceRelease --
    async function fetchActivate(requestInfo) {
      const reqBody = {
          action_name: 'activate',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('activate', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchReactivate(requestInfo) {
      const reqBody = {
          action_name: 'reactivate',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('reactivate', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchDeactivate(requestInfo) {
      const reqBody = {
          action_name: 'deactivate',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('deactivate', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchForceRelease(requestInfo) {
      const reqBody = {
          action_name: 'force_release',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('force_release', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }

    // -- Migration --
    async function fetchMigrationPreparation(requestInfo) {
      const reqBody = {
          action_name: 'migration_preparation',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('migration_preparation', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }
    async function fetchMigrate(requestInfo) {
      const reqBody = {
          action_name: 'migrate',
          object: requestInfo
      }
      try {
        const response = await apiCallWithWorker('migrate', `${api_server_url}/access`, reqBody, { needsAuth: true })
        return response;
      } catch (err) {
        throw err;
      }
    }

    return {
      setupUseApiRequest,
      fetchExampleWorker,
      fetchPing,
      fetchRegister,
      fetchRegisterConfirm,
      fetchAutoLogin,
      fetchLogin,
      fetchAuthorization,
      fetchAuthorizationRegister,
      fetchAuthorizationAutoLogin,
      fetchAuthorizationLogin,
      fetchAuthorizationExchange,
      fetchForgotPassword,
      fetchResetPassword,
      fetchSendAgain,
      fetchGetLicenses,
      fetchGetUsers,
      fetchGetProfile,
      fetchGetApplicationLicenses,
      fetchGetApplicationInfo,
      fetchRequestAccess,
      fetchAccessSharingConfirm,
      fetchAddSerialNumber,
      fetchDeleteSerialNumber,
      fetchTransferOwnership,
      fetchSendInvitation,
      fetchAddUser,
      fetchDeleteUser,
      fetchEditDelegates,
      fetchEditProfile,
      fetchUpdateEmailConfirm,
      fetchDeleteAccount,
      fetchLogout,
      fetchActivate,
      fetchReactivate,
      fetchDeactivate,
      fetchForceRelease,
      fetchMigrationPreparation,
      fetchMigrate,
    };
}

export default useApiRequest